import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { auth, db } from '../../Admin/Config/Config';
import Loading from '../../assets/Loading/Loading';

export default function OrderHistory(user) {
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [submitted, setSubmitted] = useState(true);
  const [orderDetails, setOrderDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reviewLoading, setReviewLoading] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // =============================== Control back button =============================== //

  useEffect(() => {
    const handlePopState = (event) => {
      navigate("/profile");
    };

    window.addEventListener("popstate", handlePopState);
    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!location.state) {
      navigate("/profile");
    }
    fetchOrder();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  const fetchOrder = async () => {
    await fetchFeedback();
    const docRef = doc(db, "orders", location.state.order.id);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      setOrderDetails(docSnap.data());
      setLoading(false);
    }
  };

  const handleRating = (value) => {
    setRating(value);
  };

  const fetchFeedback = async () => {
    const ref = doc(db, "orders", location.state.order.id);
    const docSnap = await getDoc(ref);
    if (docSnap.exists()) {
      setSubmitted(docSnap.data().feedback ? true : false);
    }
  };

  // =============================== Submit feedback ================================ //
  const handleEditReview = () => {
    setComment(orderDetails.feedback ? orderDetails.feedback : "");
    setRating(orderDetails.user_rating ? orderDetails.user_rating : 0);
    setSubmitted(false);
  };

  const handleSubmit = async () => {
    if (rating > 0 && comment.trim()) {
      setReviewLoading(true);
      await updateDoc(doc(db, "orders", location.state.order.id), {
        user_rating: rating,
        feedback: comment,
      });
      await setDoc(
        doc(db, "customer_reviews", location.state.order.id),
        {
          user_rating: rating,
          feedback: comment,
          user_id: auth.currentUser.uid,
          email: auth.currentUser.email,
          display: false,
          date: new Date(),
          user_name: user?.name || "Anonymous",
          new: true,
          order: 1,
        },
        {
          merge: true,
        }
      );
      setReviewLoading(false);
      setSubmitted(true);
    } else {
      alert("Please select a rating and add a comment before submitting.");
    }
  };

  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const togglePopup = () => setIsPopupOpen(!isPopupOpen);

  // Status Step Component
  const StatusStep = ({ step, currentStep, icon, label, date }) => {
    const isCompleted = currentStep >= step;
    const isCurrent = currentStep === step;

    return (
      <div className="flex flex-col items-center relative z-10 w-full sm:w-auto mb-8 sm:mb-0 group">
        {/* Outer glow ring for current step */}
        {isCurrent && (
          <div className="absolute top-0 w-16 h-16 rounded-full bg-emerald-400/20 animate-ping" style={{ animationDuration: '2s' }}></div>
        )}
        
        {/* Main icon container */}
        <div
          className={`w-16 h-16 rounded-full flex items-center justify-center transition-all duration-500 ease-out z-20 relative
            ${isCompleted
              ? "bg-gradient-to-br from-emerald-400 to-emerald-600 shadow-lg shadow-emerald-200"
              : isCurrent
                ? "bg-gradient-to-br from-emerald-400 to-emerald-500 shadow-xl shadow-emerald-200 scale-110"
                : "bg-gray-100 border-2 border-dashed border-gray-300"
            }`}
        >
          {/* Inner circle highlight */}
          {(isCompleted || isCurrent) && (
            <div className="absolute inset-1 rounded-full bg-white/20"></div>
          )}
          
          {/* Step number */}
          <span
            className={`text-2xl font-bold transition-all duration-300 relative z-10
              ${isCompleted || isCurrent
                ? "text-white drop-shadow-sm" 
                : "text-gray-400"
              }`}
          >
            {step}
          </span>

          {/* Checkmark badge for completed steps (including current) */}
          {isCompleted && (
            <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-white rounded-full flex items-center justify-center shadow-md border-2 border-emerald-500">
              <svg className="w-3 h-3 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            </div>
          )}
        </div>

        {/* Label and date section */}
        <div className={`mt-4 text-center transition-all duration-300 ${isCurrent ? "transform scale-105" : ""}`}>
          <p className={`text-sm font-semibold tracking-wide
            ${isCompleted ? "text-emerald-600" : isCurrent ? "text-emerald-700" : "text-gray-400"}`}>
            {label}
          </p>
          
          {/* Date/Time badge */}
          <div className={`mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium
            ${date 
              ? isCompleted || isCurrent
                ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                : "bg-gray-50 text-gray-500 border border-gray-200"
              : "bg-gray-50 text-gray-400 border border-dashed border-gray-300"
            }`}>
            {date ? (
              <>
                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                {new Date(date.seconds * 1000).toLocaleDateString("en-US", { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
              </>
            ) : (
              <>
                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Pending
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Status Line Component
  const StatusLine = ({ step, currentStep }) => {
    // Line connecting step N-1 to step N is green when we've completed step N-1 or reached step N
    const isCompleted = currentStep >= step;
    // Line is "active" (half-filled) when we're currently at step N-1
    const isActive = currentStep === step - 1;
    
    return (
      <div className="hidden sm:flex flex-1 items-center mx-2 relative self-start" style={{ marginTop: '30px' }}>
        {/* Background track (gray) */}
        <div className="w-full h-2 bg-gray-200 rounded-full">
          {/* Progress fill (green) */}
          <div
            className={`h-full rounded-full transition-all duration-700 ease-out ${
              isCompleted 
                ? "w-full bg-emerald-500" 
                : isActive 
                  ? "w-1/2 bg-emerald-400 animate-pulse"
                  : "w-0"
            }`}
          ></div>
        </div>
      </div>
    );
  };

  return (
    <>
      {loading ? (
        <Loading />
      ) : (
        <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
          {orderDetails ? (
            <div className="max-w-5xl mx-auto space-y-8">

              {/* Order Header Card */}
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="bg-[#BD1D00] px-6 py-4 flex flex-col sm:flex-row justify-between items-center gap-4">
                  <div className="flex items-center gap-3">
                    <div className="bg-white/10 p-2 rounded-lg">
                      <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-white/60 text-xs uppercase tracking-wider font-semibold">Order ID</p>
                      <h1 className="text-white text-xl font-bold tracking-wide">#{orderDetails.order_id}</h1>
                    </div>
                  </div>
                  <button
                    onClick={togglePopup}
                    className="group bg-white/10 hover:bg-white/20 text-white px-5 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200 flex items-center gap-2 backdrop-blur-sm border border-white/10"
                  >
                    View Items
                    <svg className="w-4 h-4 transition-transform group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>

                {/* Order Meta Info */}
                <div className="p-6 grid grid-cols-1 sm:grid-cols-3 gap-6 divide-y sm:divide-y-0 sm:divide-x divide-gray-100">
                  <div className="text-center sm:text-left p-2">
                    <p className="text-sm text-gray-400 font-medium mb-1">Date Placed</p>
                    <p className="text-gray-800 font-semibold text-lg">
                      {new Date(orderDetails.orderDate.seconds * 1000).toLocaleDateString("en-US", { year: 'numeric', month: 'long', day: 'numeric' })}
                    </p>
                  </div>
                  <div className="text-center sm:text-left p-2 sm:pl-6">
                    <p className="text-sm text-gray-400 font-medium mb-1">Total Amount</p>
                    <p className="text-green-600 font-bold text-lg">
                      ${parseFloat(orderDetails.offerPriceTotal).toLocaleString()}
                    </p>
                  </div>
                  <div className="text-center sm:text-left p-2 sm:pl-6">
                    <p className="text-sm text-gray-400 font-medium mb-1">Total Items</p>
                    <div className="flex items-center justify-center sm:justify-start gap-2">
                      <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" /></svg>
                      <span className="text-gray-800 font-medium">{orderDetails.items?.length || 0} Items</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Status Tracker */}
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                {/* Header with gradient */}
                <div className="bg-gradient-to-r from-emerald-500 to-teal-500 px-6 py-4">
                  <div className="flex items-center gap-3">
                    <div className="bg-white/20 p-2 rounded-lg backdrop-blur-sm">
                      <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-white font-bold text-lg">Order Status</h3>
                      <p className="text-emerald-100 text-xs">Track your order progress</p>
                    </div>
                  </div>
                </div>

                {/* Status content */}
                <div className="p-8 relative">
                  {/* Decorative background */}
                  <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-to-br from-emerald-50 to-teal-50 rounded-full -mr-20 -mt-20 opacity-50"></div>
                  <div className="absolute bottom-0 left-0 w-32 h-32 bg-gradient-to-tr from-emerald-50 to-teal-50 rounded-full -ml-16 -mb-16 opacity-50"></div>

                  {orderDetails.order_status === "cancelled" ? (
                    <div className="flex flex-col sm:flex-row items-center justify-center p-8 bg-gradient-to-br from-red-50 to-rose-50 rounded-2xl border border-red-100 relative overflow-hidden">
                      {/* Decorative X pattern */}
                      <div className="absolute inset-0 opacity-5">
                        <div className="absolute top-4 left-4 w-8 h-8 border-2 border-red-500 rounded-full"></div>
                        <div className="absolute bottom-4 right-4 w-12 h-12 border-2 border-red-500 rounded-full"></div>
                      </div>
                      
                      <div className="bg-gradient-to-br from-red-400 to-rose-500 p-4 rounded-2xl shadow-lg shadow-red-200 mr-0 sm:mr-6 mb-4 sm:mb-0">
                        <svg className="w-10 h-10 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </div>
                      <div className="text-center sm:text-left">
                        <h4 className="text-2xl font-bold text-red-700 mb-1">Order Cancelled</h4>
                        <p className="text-red-500 text-sm">This order has been cancelled and refunded.</p>
                        <div className="mt-3 inline-flex items-center gap-2 text-xs text-red-600 bg-red-100 px-3 py-1.5 rounded-full">
                          <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          Cancelled on {orderDetails.cancelled_date ? new Date(orderDetails.cancelled_date.seconds * 1000).toLocaleDateString("en-US", { month: 'short', day: 'numeric' }) : 'N/A'}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center relative py-4">


                    <StatusStep
                      step={1}
                      currentStep={orderDetails.delivery_step}
                      icon="bi bi-check-lg"
                      label="Placed"
                      date={orderDetails.orderDate}
                    />
                    <StatusLine step={2} currentStep={orderDetails.delivery_step} />

                    <StatusStep
                      step={2}
                      currentStep={orderDetails.delivery_step}
                      icon="bi bi-box-seam"
                      label="Processed"
                      date={orderDetails.processed_date}
                    />
                    <StatusLine step={3} currentStep={orderDetails.delivery_step} />

                    <StatusStep
                      step={3}
                      currentStep={orderDetails.delivery_step}
                      icon="bi bi-truck"
                      label="Shipped"
                      date={orderDetails.shipped_date}
                    />
                    <StatusLine step={4} currentStep={orderDetails.delivery_step} />

                    <StatusStep
                      step={4}
                      currentStep={orderDetails.delivery_step}
                      icon="bi bi-house-door"
                      label="Delivered"
                      date={orderDetails.delivered_date}
                    />
                  </div>
                )}
                </div>
              </div>

              {/* Review Section */}
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 bg-yellow-50 rounded-bl-full -mr-10 -mt-10 opacity-50"></div>

                <h3 className="text-lg font-bold text-gray-800 mb-6 flex items-center gap-2 relative z-10">
                  <span className="w-1 h-6 bg-yellow-400 rounded-full"></span>
                  Rate Your Experience
                </h3>

                {submitted ? (
                  <div className="bg-green-50 border border-green-100 rounded-xl p-6 flex items-center gap-4">
                    <div className="bg-green-100 p-2 rounded-full">
                      <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                    </div>
                    <div>
                      <p className="text-green-800 font-semibold text-lg">Thank you for your feedback!</p>
                      <button
                        onClick={handleEditReview}
                        className="text-green-600 text-sm hover:text-green-800 underline font-medium mt-1 inline-flex items-center gap-1"
                      >
                        Edit your review
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="max-w-2xl">
                    <div className="flex space-x-3 mb-6">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <button
                          key={star}
                          onClick={() => handleRating(star)}
                          className={`transform hover:scale-110 transition-all duration-200 focus:outline-none ${rating >= star ? "text-yellow-400" : "text-gray-200 hover:text-yellow-200"
                            }`}
                        >
                          <svg className="w-10 h-10 fill-current" viewBox="0 0 24 24">
                            <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
                          </svg>
                        </button>
                      ))}
                    </div>

                    <p className="text-gray-500 font-medium mb-4 text-sm uppercase tracking-wide">
                      {rating > 0 ? `Rating: ${rating}/5` : "Tap a star to rate"}
                    </p>

                    <div className="relative">
                      <textarea
                        className="w-full bg-gray-50 p-4 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500 transition-all text-gray-700 min-h-[120px] resize-none"
                        placeholder="Tell us about your experience..."
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                      />
                    </div>

                    <button
                      onClick={handleSubmit}
                      disabled={reviewLoading}
                      className="mt-4 px-8 py-3 bg-red-600 hover:bg-red-700 text-white font-bold rounded-xl shadow-lg shadow-red-500/30 transition-all hover:translate-y-[-2px] disabled:opacity-70 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                      {reviewLoading ? (
                        <>
                          <svg className="animate-spin h-5 w-5 text-white" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                          </svg>
                          Submitting...
                        </>
                      ) : (
                        "Submit Review"
                      )}
                    </button>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="text-center py-12 text-gray-500">Order not found.</div>
          )}

          {/* Items Popup */}
          {isPopupOpen && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm transition-opacity">
              <div className="bg-white rounded-2xl shadow-2xl lg:w-[60%] w-[90%] max-w-2xl max-h-[85vh] flex flex-col overflow-hidden">

                {/* Popup Header */}
                <div className="p-5 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
                  <h3 className="text-xl font-bold text-gray-800">Topic Included</h3>
                  <button
                    onClick={togglePopup}
                    className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-red-50 text-gray-400 hover:text-red-500 transition-colors"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                {/* Popup Content */}
                <div className="overflow-y-auto p-6 space-y-4">
                  {orderDetails && orderDetails.items.map((item, index) => (
                    <div className=''>
                      <div key={index} className="flex gap-4 p-4 rounded-xl border border-gray-100 bg-white hover:border-red-100 hover:shadow-sm transition-all h-[100px]">
                        <div className="w-20 h-20 bg-gray-50 rounded-lg flex-shrink-0 p-2">
                          <img
                            src={item.thumb_url}
                            alt={item.product_name}
                            className="w-full h-full object-contain"
                          />
                        </div>
                        <div className="flex-1">
                          <h4 className="text-gray-800 font-semibold text-sm sm:text-base line-clamp-2">
                            {item.product_name}
                          </h4>
                          <div className="flex items-center gap-4 mt-2">
                            <span className="text-xs font-bold text-gray-500 bg-gray-100 px-2 py-1 rounded">
                              Qty: {item.cart_quantity}
                            </span>
                            <span className="text-sm font-bold text-red-600">
                              ${item.offer_price}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                  ))}


                  {/* Pricing Summary */}
                  <div className="mt-6 bg-gray-50 rounded-xl p-5 space-y-2 text-sm">
                    <div className="flex justify-between text-gray-600">
                      <span>Subtotal</span>
                      <span className="font-medium">${(parseFloat(orderDetails?.offerPriceTotal || 0) - parseFloat(orderDetails?.tax || 0) - parseFloat(orderDetails?.delivery_charge || 0)).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-gray-600">
                      <span>Tax</span>
                      <span className="font-medium">${parseFloat(orderDetails?.tax || 0).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-gray-600">
                      <span>Delivery Charge</span>
                      <span className="font-medium">${parseFloat(orderDetails?.delivery_charge || 0).toFixed(2)}</span>
                    </div>
                    <div className="border-t border-gray-200 my-2 pt-2 flex justify-between text-gray-900 font-bold text-base">
                      <span>Total</span>
                      <span>${parseFloat(orderDetails?.offerPriceTotal).toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
}
